<template>
	<app-modal @close="closeModal(existingProducts)" headerClass="px-4 py-0">
		<template v-slot:header>
			<app-input
				class="flex flex-1"
				name="search"
				v-model="search"
				placeholder="Search for product"
				icon="search"
				:hide-label="true"
			/>
		</template>
		<div class="products-modal-header">
			<app-scrollable>
				<div class="flex flex-row overflow-none">
					<div
						class="flex flex-1 pt-1 pb-2 mx-1 items-center border-b-2 justify-center cursor-pointer"
						:class="{
							'border-primary': category.id === selectedCategory,
							'border-border-color': category.id !== selectedCategory,
						}"
						style="min-width: 120px;"
						v-for="(category, index) in categories"
						:key="index"
						@click="filterByCategory(category)"
					>
						<div class="flex text-center">{{ category.menuCategory.categoryName }}</div>
					</div>
				</div>
			</app-scrollable>
			<p class="text-18 font-bold">Products:</p>
			<div class="flex flex-1 flex-row my-2" v-for="product in filteredProducts" :key="product.id">
				<div class="w-1/6">
					<img v-if="product.image" :src="$filters.setImage(product.image)" alt="product-image" />
					<img v-else src="../../../assets/images/bab10vite.png" class="p-2" alt="logo" />
				</div>
				<div class="flex flex-1 flex-col justify-center ml-2">
					<p>{{ product['itemName'] }}</p>
					<p>{{ product['itemDescription'] }}</p>
				</div>
				<div class="flex items-center ml-2">
					<span v-if="product.quantity" class="bg-success text-white rounded-full w-6 h-6 text-center">
						{{ product.quantity }}
					</span>
				</div>
				<div class="flex items-center ml-2">
					<app-icon
						@click="removeProduct(product)"
						class="cursor-pointer border-success border-1 rounded-full text-success"
						icon="minus"
					/>
					<span class="px-2">{{ $filters.formatCurrency(product['itemPrice'], restaurantCurrency) }}</span>
					<app-icon
						@click="addProduct(product)"
						class="cursor-pointer border-success border-1 rounded-full text-success"
						icon="plus"
					/>
				</div>
			</div>
		</div>
		<template v-slot:footer>
			<div
				:class="
					`grid grid-cols-3 justify-center p-4 bg-success text-white rounded-t-md
					${totalItems !== 0 ? 'cursor-pointer hover:bg-green-700 active:bg-green-700' : 'cursor-not-allowed bg-green-400'}`
				"
				@click="addToOrder"
			>
				<div>
					<app-icon icon="shopping-basket" class="text-white" />
					<sup> &nbsp;&nbsp;{{ totalItems }} </sup>
				</div>
				<div class="grid place-self-center">
					<span>
						Add Products
					</span>
				</div>
				<div class="grid place-self-end">
					<span>
						{{ $filters.formatCurrency(totalPrice, restaurantCurrency) }}
					</span>
				</div>
			</div>
		</template>
	</app-modal>
</template>

<script>
	import AppModal from '@/components/app/AppModal';
	import RestaurantService from '@/services/sales/BusinessService';
	import AppIcon from '@/components/app/AppIcon';
	import OrderService from '@/services/OrderService';
	import AppScrollable from '@/components/app/AppScrollable';

	export default {
		name: 'ProductsModal',
		components: { AppModal, AppIcon, AppScrollable },
		emits: ['close'],
		props: ['restaurantId', 'customerId', 'order', 'existingProducts', 'restaurantCurrency'],
		computed: {
			filteredProducts() {
				let results = [...this.products];
				if (this.selectedCategory) {
					results = results.filter((p) => p['menuRestaurantCategory'].id === this.selectedCategory);
				}
				if (!this.search) {
					return results;
				}
				const search = this.search.toLowerCase();
				return results.filter(
					(p) =>
						p['itemName'].toLowerCase().indexOf(search) > -1 ||
						p['itemDescription'].toLowerCase().indexOf(search) > -1
				);
			},
			totalItems() {
				const items = this.products.filter((p) => p.quantity > 0).map((p) => p.quantity);
				return items.reduce(function(a, b) {
					return a + b;
				}, 0);
			},
			totalPrice() {
				const items = this.products.filter((p) => p.quantity > 0).map((p) => p.quantity * p['itemPrice']);
				return items.reduce(function(a, b) {
					return a + b;
				}, 0);
			},
		},
		data: () => ({
			loading: false,
			loadingProducts: false,
			search: '',
			selectedCategory: 0,
			products: [],
			categories: [],
		}),
		methods: {
			getProducts() {
				this.emitter.emit('isLoading', true);
				RestaurantService.getProducts(this.restaurantId, this.customerId)
					.then((products) => {
						this.products = products.map((p) => {
							const existing =
								this.existingProducts && this.existingProducts.find((prd) => p.id === prd.id);
							return {
								...p,
								quantity: existing ? existing.quantity : 0,
							};
						});
						this.categories = this.extractCategoriesFromProducts(products);
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.emitter.emit('isLoading', false);
					});
			},
			extractCategoriesFromProducts(products) {
				const cat = [{ id: 0, menuCategory: { categoryName: 'All' } }];
				products.map((p) => {
					const i = cat.findIndex((c) => c.id === p['menuRestaurantCategory'].id);
					if (i === -1) {
						cat.push(p['menuRestaurantCategory']);
					}
				});
				return cat;
			},
			filterByCategory({ id }) {
				this.selectedCategory = id;
			},
			closeModal(products) {
				this.$emit('close', products);
			},
			addProduct(product) {
				if (!product.quantity) {
					product.quantity = 1;
				} else {
					product.quantity += 1;
				}
			},
			removeProduct(product) {
				if (product.quantity) {
					product.quantity -= 1;
				} else {
					product.quantity = 0;
				}
			},
			addToOrder() {
				const products = this.products.filter((p) => p.quantity > 0);
				if (this.order) {
					this.loading = true;
					OrderService.addProducts(this.order['orderID'], products).then(
						() => {
							this.loading = false;
							this.closeModal(products);
						},
						(error) => {
							this.$notificationService.error(error);
						}
					);
				} else {
					this.closeModal(products);
				}
			},
		},
		mounted() {
			this.getProducts();
		},
	};
</script>

<style scoped>
	.products-modal-header {
		width: 50rem;
		max-width: 50rem;
		height: 37.5rem;

		@media only screen and (max-height: 700px) {
			height: 18rem;
		}
		@media only screen and (max-height: 450px) {
			height: 10rem;
		}
		@media only screen and (max-width: 810px) {
			width: 30rem;
			max-width: 30rem;
		}
		@media only screen and (max-width: 500px) {
			width: 22rem;
			max-width: 22rem;
		}
	}
</style>
